import React from 'react';
import ImageDisplay from './ImageDisplay';
import YearSlider from './YearSlider';
import YearInput from './YearInput';
import GameControls from './GameControls';
import FeedbackDisplay from './FeedbackDisplay';
import AnimatedDescription from './AnimatedDescription';
import { WikimediaImage, GuessHistoryItem } from '../types/types';

interface GameContentProps {
  // Image related props
  image: WikimediaImage | null;
  round?: number;
  totalRounds?: number;
  censoredDescription: string;
  censoredFilename: string;
  imageLoading?: boolean;

  // Game state related props
  yearInput: string[];
  hasGuessed: boolean;
  guessHistory: GuessHistoryItem[];
  revealedDigits: boolean[];
  challengeRevealedDigits: boolean[];
  validationError: string;
  hintMessage: string;
  hintRequested: boolean;
  selectingDigit: boolean;
  allDigitsRevealed: boolean;
  pointsGained: number | null;
  skipsLeft?: number;
  showSkipButton?: boolean;
  isLastRound?: boolean;

  // User interaction handlers
  onDigitInput: (index: number, value: string) => void;
  onKeyDown: (index: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onTimelineClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onSliderChange: (year: number) => void;
  onGuess: () => void;
  onHint: () => void;
  onDigitReveal: (index: number) => void;
  onSkip?: () => void;
  onNext: () => void;

  // Configuration
  minYear: number;
  maxYear: number;
}

const GameContent: React.FC<GameContentProps> = ({
  // Image related props
  image,
  round,
  totalRounds = 10,
  censoredDescription,
  censoredFilename,
  imageLoading,

  // Game state related props
  yearInput,
  hasGuessed,
  guessHistory,
  revealedDigits,
  challengeRevealedDigits,
  validationError,
  hintMessage,
  hintRequested,
  selectingDigit,
  allDigitsRevealed,
  pointsGained,
  skipsLeft = 0,
  showSkipButton = false,
  isLastRound = false,

  // User interaction handlers
  onDigitInput,
  onKeyDown,
  onPaste,
  onTimelineClick,
  onSliderChange,
  onGuess,
  onHint,
  onDigitReveal,
  onSkip = () => {},
  onNext,

  // Configuration
  minYear,
  maxYear,
}) => {
  if (!image) return null;

  const guessedYear = guessHistory.length > 0 ? guessHistory[0].year : null;

  // Get the appropriate descriptions for before and after guessing
  const beforeDescription = censoredDescription;
  const afterDescription = image.revealedDescription || image.description || '';

  return (
    <>
      <div className="flex flex-col h-[calc(100dvh-72px)] md:h-auto">
        <div className="px-0 md:px-6 flex-grow-0">
          {imageLoading ? (
            <div className="border-y md:border border-[#322A3F] md:rounded-t-sm overflow-hidden flex items-center justify-center h-[calc(100dvh-396px)] md:h-auto bg-[#18121d] animate-pulse">
              <div className="relative z-10 block mx-auto max-h-[calc(100dvh-396px)] md:max-h-[40dvh] md:min-h-[40dvh] w-full bg-[#322A3F]/50"></div>
            </div>
          ) : (
            image && (
              <ImageDisplay
                round={round}
                totalRounds={totalRounds}
                imageUrl={image.url}
                alt="Guess the year"
                captionBeforeText={beforeDescription}
                captionAfterText={afterDescription}
                hasGuessed={hasGuessed}
              />
            )
          )}
        </div>
        <div className="flex flex-col flex-grow overflow-auto">
          <div className="px-2 md:px-6">
            {/* Image Metadata with animated description transition */}
            {image && (
              <div className="mt-2 text-white/70 text-xs text-left">
                <AnimatedDescription
                  key={`desc-${round}`}
                  beforeText={beforeDescription}
                  afterText={afterDescription}
                  hasGuessed={hasGuessed}
                />
              </div>
            )}
          </div>

          <div
            className={`flex justify-center mt-3 px-6 pb-6 md:pb-7 ${hasGuessed ? 'overflow-hidden' : ''}`}
          >
            <div className="w-full">
              <div className="w-[233px] justify-self-center mx-auto">
                {/* Only show YearInput if user hasn't guessed yet */}
                {!hasGuessed && (
                  <YearInput
                    yearInput={yearInput}
                    onDigitInput={onDigitInput}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    disabled={hasGuessed}
                    hasGuessed={hasGuessed}
                    onEnterPress={onGuess}
                    hintRequested={hintRequested}
                    selectingDigit={selectingDigit}
                    revealedDigits={revealedDigits}
                    challengeRevealedDigits={challengeRevealedDigits}
                    onDigitReveal={onDigitReveal}
                    validationError={validationError}
                    hintMessage={hintMessage}
                  />
                )}
              </div>

              <YearSlider
                lowerBound={minYear}
                upperBound={maxYear}
                minYear={minYear}
                maxYear={maxYear}
                guessHistory={guessHistory}
                actualYear={hasGuessed ? image.year : null}
                onTimelineClick={onTimelineClick}
                yearInput={yearInput}
                onSliderChange={onSliderChange}
                revealedDigits={revealedDigits}
                disabled={selectingDigit}
                isSelectingDigit={selectingDigit}
              />

              {/* Feedback Display - show only when user has guessed */}
              {hasGuessed && image && (
                <FeedbackDisplay
                  hasGuessed={hasGuessed}
                  actualYear={image.year}
                  guessYear={guessedYear}
                  pointsGained={pointsGained || 0}
                />
              )}

              <div className="w-full md:w-[233px] justify-self-center mx-auto">
                <GameControls
                  hasGuessed={hasGuessed}
                  hintRequested={hintRequested}
                  skipsLeft={skipsLeft}
                  onGuess={onGuess}
                  onHint={onHint}
                  onSkip={onSkip}
                  onNext={onNext}
                  showSkipButton={showSkipButton}
                  allDigitsRevealed={allDigitsRevealed}
                  isLastRound={isLastRound}
                  selectingDigit={selectingDigit}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(GameContent);
